import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { coachAPI, adaptiveAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CoachLayout from './CoachLayout';
import HitLFeedPanel from '../../../../shared/components/HitLFeed/HitLFeedPanel';
import LearnerSkillCardsPanel from './LearnerSkillCardsPanel';

// ── Skill Mastery Table (coach view of a learner's BKT/FSRS state) ─────────

const SKILL_TIER_COLORS = {
  cold_start:  '#ef4444',
  emerging:    '#ef4444',
  developing:  '#f59e0b',
  proficient:  '#22c55e',
  mastered:    '#14b8a6',
};

function SkillMasteryTable({ learnerId }) {
  const [skills, setSkills]         = useState(null);
  const [reviewQueue, setReviewQueue] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!learnerId) return;
    setLoading(true);
    Promise.all([
      adaptiveAPI.getLearnerMasteryForCoach(learnerId).catch(() => ({ data: null })),
      adaptiveAPI.getReviewQueue(learnerId).catch(() => ({ data: null })),
    ]).then(([masteryRes, queueRes]) => {
      const raw = masteryRes?.data;
      setSkills(Array.isArray(raw) ? raw : (raw?.skills || []));
      setReviewQueue(queueRes?.data?.queue || []);
    }).finally(() => setLoading(false));
  }, [learnerId]);

  const dueBySkill = {};
  (reviewQueue || []).forEach((item) => {
    if (item.skill_id) dueBySkill[item.skill_id] = item;
  });

  // Group by domain
  const byDomain = {};
  (skills || []).forEach((s) => {
    const d = s.domain_id || s.domain || 'General';
    (byDomain[d] = byDomain[d] || []).push(s);
  });

  return (
    <div data-testid="coach-skill-mastery-table">
      {loading && (
        <div style={{ color: '#64748b', fontSize: 12, padding: '8px 0' }}>Loading skill mastery…</div>
      )}
      {!loading && (!skills || skills.length === 0) && (
        <div style={{ color: '#64748b', fontSize: 12, padding: '8px 0' }}>
          No skill-level mastery data yet. Learner needs to complete at least one session.
        </div>
      )}
      {Object.entries(byDomain).map(([domain, domSkills]) => (
        <div key={domain} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
            {domain.replace(/_/g, ' ')}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={{ paddingBottom: 4, fontWeight: 600 }}>Skill</th>
                <th style={{ paddingBottom: 4, fontWeight: 600, width: 90 }}>Tier</th>
                <th style={{ paddingBottom: 4, fontWeight: 600, width: 70 }}>P(Mastery)</th>
                <th style={{ paddingBottom: 4, fontWeight: 600, width: 70 }}>Reviews Due</th>
              </tr>
            </thead>
            <tbody>
              {domSkills.map((s) => {
                const tier = s.tier || s.mastery_tier || 'cold_start';
                const color = SKILL_TIER_COLORS[tier] || '#ef4444';
                const pMastery = s.p_mastery ?? s.p_l ?? null;
                const due = dueBySkill[s.skill_id || s.id];
                return (
                  <tr
                    key={s.skill_id || s.id}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
                    data-testid={`skill-row-${s.skill_id || s.id}`}
                  >
                    <td style={{ padding: '5px 0', color: '#cbd5e1' }}>
                      {s.skill_label || s.label || s.name || (s.skill_id || '').split('.').pop()}
                    </td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        padding: '1px 8px',
                        borderRadius: 999,
                        fontSize: 10,
                        fontWeight: 700,
                        color,
                        background: `${color}22`,
                        border: `1px solid ${color}44`,
                      }}>
                        {tier.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ color: '#94a3b8' }}>
                      {pMastery != null ? `${Math.round(pMastery * 100)}%` : '—'}
                    </td>
                    <td>
                      {due ? (
                        <span style={{ color: '#fbbf24', fontWeight: 700 }}>
                          {due.days_overdue > 0 ? `${due.days_overdue.toFixed(0)}d overdue` : 'today'}
                        </span>
                      ) : (
                        <span style={{ color: '#475569' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/* /coach/my-domains returns either:
 *   { domains: [{ domain: "football", role: "coach", ... }, ...] }   ← real shape
 *   { domains: ["football", "basketball"] }                          ← defensive
 *   { domains: { football: {...} } }                                 ← legacy
 * Calling Object.keys() on the first form returns array INDICES ("0","1"),
 * which is why the Enroll dropdown was rendering "0" instead of "Football".
 */
function extractDomainIds(payload) {
  const raw = payload?.domains;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((d) => (typeof d === 'string' ? d : d?.domain))
      .filter(Boolean);
  }
  if (typeof raw === 'object') return Object.keys(raw);
  return [];
}

/* ── Enroll directly by email ──────────────────────────────────────────────── */
function EnrollModal({ onDone, onClose }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [domain, setDomain] = useState('football');
  const [domains, setDomains] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    coachAPI.listMyDomains()
      .then(({ data }) => {
        const list = extractDomainIds(data);
        if (list.length) { setDomains(list); setDomain(list[0]); }
      })
      .catch(() => setDomains(['football']));
  }, []);

  const handleEnroll = async () => {
    if (!email.trim()) return;
    setSaving(true); setError(null);
    try {
      const { data } = await coachAPI.enrollLearner({
        email: email.trim().toLowerCase(),
        display_name: displayName.trim() || undefined,
        domain,
      });
      setResult(data);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to enroll. Check the email and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">Enroll a Learner Directly</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Enter the learner's email to enroll them immediately — no approval step needed.
          If they don't have an account yet, one will be created for them.
        </p>
        {result ? (
          <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: '#4ade80', marginBottom: 4 }}>
              {result.created ? 'Account created & enrolled' : 'Enrolled successfully'}
            </div>
            <div className="text-sm text-muted">{result.message}</div>
            {result.temporary_password && (
              <div style={{ marginTop: 12, fontSize: '0.9rem' }}>
                <strong>One-time password (copy now; not shown again):</strong>
                <code style={{ display: 'block', marginTop: 6, wordBreak: 'break-all' }}>{result.temporary_password}</code>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="coach-form-group">
              <label className="coach-label">Email address *</label>
              <input className="coach-input" type="email" placeholder="athlete@example.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Full name (optional)</label>
              <input className="coach-input" placeholder="Jane Doe" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              <span className="text-muted text-sm">If blank, derived from email.</span>
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Domain</label>
              <select className="coach-select" value={domain} onChange={(e) => setDomain(e.target.value)}>
                {(domains.length ? domains : ['football']).map((d) => (
                  <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                ))}
              </select>
            </div>
            {error && <div className="coach-error" style={{ marginBottom: 12 }}>{error}</div>}
          </>
        )}
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            <button className="btn btn-primary" onClick={handleEnroll} disabled={saving || !email.trim()}>
              {saving ? 'Enrolling…' : 'Enroll Learner'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Add Coach modal ────────────────────────────────────────────────────────── */
function AddCoachModal({ onDone, onClose, domains }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [domain, setDomain] = useState(domains[0] || '');
  const [domainRole, setDomainRole] = useState('domain_editor');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAdd = async () => {
    if (!email.trim() || !domain) return;
    setSaving(true); setError(null);
    try {
      const { data } = await coachAPI.addDomainCoach(domain, {
        email: email.trim().toLowerCase(),
        display_name: displayName.trim() || undefined,
        domain_role: domainRole,
      });
      setResult(data);
      onDone();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add coach. Check the email and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coach-modal-overlay" onClick={onClose}>
      <div className="coach-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h3 className="coach-modal-title">Add a Coach</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
          Add a coach to a domain. If they don't have an account, one will be created
          and they'll be promoted to coach automatically.
        </p>
        {result ? (
          <div style={{ background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, color: '#4ade80', marginBottom: 4 }}>
              {result.created ? 'Account created & granted access' : 'Access granted'}
            </div>
            <div className="text-sm text-muted">{result.message}</div>
            {result.temporary_password && (
              <div style={{ marginTop: 12, fontSize: '0.9rem' }}>
                <strong>One-time password (copy now; not shown again):</strong>
                <code style={{ display: 'block', marginTop: 6, wordBreak: 'break-all' }}>{result.temporary_password}</code>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="coach-form-group">
              <label className="coach-label">Email address *</label>
              <input className="coach-input" type="email" placeholder="coach@example.com" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Full name (for new accounts)</label>
              <input className="coach-input" placeholder="Jane Doe" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              <span className="text-muted text-sm">If user already exists, this is ignored.</span>
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Domain</label>
              <select className="coach-select" value={domain} onChange={(e) => setDomain(e.target.value)}>
                {domains.map((d) => (
                  <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1).replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div className="coach-form-group">
              <label className="coach-label">Domain Role</label>
              <select className="coach-select" value={domainRole} onChange={(e) => setDomainRole(e.target.value)}>
                <option value="domain_admin">Domain Admin — full control</option>
                <option value="domain_editor">Domain Editor — can edit content</option>
                <option value="domain_viewer">Domain Viewer — read-only</option>
              </select>
            </div>
            {error && <div className="coach-error" style={{ marginBottom: 12 }}>{error}</div>}
          </>
        )}
        <div className="coach-modal-actions">
          <button className="btn btn-outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            <button className="btn btn-primary" onClick={handleAdd} disabled={saving || !email.trim() || !domain}>
              {saving ? 'Adding…' : 'Add Coach'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Main Page ─────────────────────────────────────────────────────────────── */
export default function LearnerManagementPage() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('enrolled'); // 'enrolled' | 'requests' | 'coaches'
  const [filterDomain, setFilterDomain] = useState('all');
  const [myDomains, setMyDomains] = useState([]);
  const [myDomainEntitlements, setMyDomainEntitlements] = useState([]);

  // Enrolled tab state
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [learnersLoading, setLearnersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEnroll, setShowEnroll] = useState(false);

  // Requests tab state
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);

  // Coaches tab state
  const [coaches, setCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [showAddCoach, setShowAddCoach] = useState(false);
  const [coachSearch, setCoachSearch] = useState('');

  // Subscription tier saving state: { "<learnerId>:<domain>": "saving"|"saved"|null }
  const [tierSaveState, setTierSaveState] = useState({});

  // Can this user manage coaches? (admin or domain_admin on any domain)
  const canManageCoaches = isAdmin || myDomainEntitlements.some((e) => e.role === 'domain_admin');

  // Load coach's domains once
  useEffect(() => {
    coachAPI.listMyDomains()
      .then(({ data }) => {
        setMyDomains(extractDomainIds(data));
        const raw = data?.domains;
        if (Array.isArray(raw)) {
          setMyDomainEntitlements(raw.filter((d) => typeof d === 'object'));
        }
      })
      .catch(() => setMyDomains(['football']));
  }, []);

  const loadLearners = useCallback(() => {
    setLearnersLoading(true);
    const domain = filterDomain !== 'all' ? filterDomain : undefined;
    coachAPI.listLearners(domain)
      .then(({ data }) => setLearners(data.learners || []))
      .catch(() => setLearners([]))
      .finally(() => setLearnersLoading(false));
  }, [filterDomain]);

  const loadRequests = useCallback(() => {
    setRequestsLoading(true);
    const params = filterDomain !== 'all' ? { domain: filterDomain, status: 'pending' } : { status: 'pending' };
    coachAPI.listEnrollmentRequests(params)
      .then(({ data }) => setRequests(data.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setRequestsLoading(false));
  }, [filterDomain]);

  const loadCoaches = useCallback(() => {
    if (!canManageCoaches) return;
    const domain = filterDomain !== 'all' ? filterDomain : myDomains[0];
    if (!domain) return;
    setCoachesLoading(true);
    coachAPI.listDomainCoaches(domain)
      .then(({ data }) => setCoaches(data.coaches || []))
      .catch(() => setCoaches([]))
      .finally(() => setCoachesLoading(false));
  }, [filterDomain, myDomains, canManageCoaches]);

  useEffect(() => { loadLearners(); }, [loadLearners]);
  useEffect(() => { loadRequests(); }, [loadRequests]);
  useEffect(() => { if (activeTab === 'coaches') loadCoaches(); }, [activeTab, loadCoaches]);

  const viewStats = (learner) => {
    setSelectedLearner(learner);
    setStats(null);
    setStatsLoading(true);
    const domain = filterDomain !== 'all' ? filterDomain : (learner.enrolled_domains || [])[0] || 'football';
    coachAPI.getLearnerStats(learner.learner_id, domain)
      .then(({ data }) => setStats(data))
      .catch(() => setStats({ error: true }))
      .finally(() => setStatsLoading(false));
  };

  const handleApprove = async (requestId) => {
    await coachAPI.approveEnrollment(requestId).catch(() => {});
    loadRequests();
    loadLearners();
  };

  const handleReject = async (requestId, reason) => {
    await coachAPI.rejectEnrollment(requestId, reason).catch(() => {});
    loadRequests();
    loadLearners();
  };

  const handleChangeCoachDomainRole = async (coachId, domain, newRole) => {
    try {
      await coachAPI.updateDomainCoachRole(domain, coachId, newRole);
      loadCoaches();
    } catch { /* ignore */ }
  };

  const handleRemoveCoach = async (coachId, domain) => {
    if (!window.confirm('Remove this coach from the domain?')) return;
    try {
      await coachAPI.removeDomainCoach(domain, coachId);
      loadCoaches();
    } catch { /* ignore */ }
  };

  const handleChangeAccountRole = async (learnerId, newRole) => {
    try {
      await coachAPI.adminUpdateUserRole(learnerId, newRole);
      loadLearners();
    } catch { /* ignore */ }
  };

  const handleSetSubscriptionTier = async (learnerId, domain, tier) => {
    const key = `${learnerId}:${domain}`;
    setTierSaveState((prev) => ({ ...prev, [key]: 'saving' }));
    try {
      await coachAPI.setLearnerSubscriptionTier(learnerId, domain, tier);
      // Optimistically update both the panel and the list
      const isRevoked = tier === 'none';
      setSelectedLearner((prev) => {
        if (!prev || prev.learner_id !== learnerId) return prev;
        const updatedTiers = { ...(prev.domain_tiers || {}), [domain]: tier };
        const updatedEnrolled = isRevoked
          ? (prev.enrolled_domains || []).filter((d) => d !== domain)
          : [...new Set([...(prev.enrolled_domains || []), domain])];
        return { ...prev, domain_tiers: updatedTiers, enrolled_domains: updatedEnrolled };
      });
      setLearners((prev) => prev.map((l) => {
        if (l.learner_id !== learnerId) return l;
        const updatedEnrolled = isRevoked
          ? (l.enrolled_domains || []).filter((d) => d !== domain)
          : [...new Set([...(l.enrolled_domains || []), domain])];
        return { ...l, domain_tiers: { ...(l.domain_tiers || {}), [domain]: tier }, enrolled_domains: updatedEnrolled };
      }));
      setTierSaveState((prev) => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => setTierSaveState((prev) => ({ ...prev, [key]: null })), 2000);
    } catch (err) {
      setTierSaveState((prev) => ({ ...prev, [key]: null }));
      alert(err.response?.data?.detail || 'Failed to update access. Check that you have coach entitlement for this domain.');
    }
  };

  const filteredCoaches = coaches.filter((c) => {
    const q = coachSearch.toLowerCase();
    return !q || c.display_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  const requestItems = requests.map((r) => ({
    id: r.request_id,
    title: r.learner_name || r.learner_id,
    summary: `${r.learner_email || ''} wants access to domain: ${r.domain}`,
    type: 'enrollment',
    priority: 'medium',
    createdAt: r.requested_at,
    payload: r,
  }));

  const filtered = learners.filter((l) => {
    const q = search.toLowerCase();
    return !q || l.display_name?.toLowerCase().includes(q) || l.email?.toLowerCase().includes(q);
  });

  const domainOptions = ['all', ...myDomains];
  const pendingCount = requests.length;

  return (
    <CoachLayout title="Team">
      {showEnroll && (
        <EnrollModal onDone={loadLearners} onClose={() => setShowEnroll(false)} />
      )}
      {showAddCoach && (
        <AddCoachModal
          onDone={loadCoaches}
          onClose={() => setShowAddCoach(false)}
          domains={myDomains}
        />
      )}

      {/* ── Domain filter + tab bar ────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`btn ${activeTab === 'enrolled' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('enrolled')}
          >
            Learners ({learners.length})
          </button>
          <button
            className={`btn ${activeTab === 'requests' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setActiveTab('requests')}
            style={{ position: 'relative' }}
          >
            Access Requests
            {pendingCount > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6,
                background: '#ef4444', color: '#fff', borderRadius: 9999,
                fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', lineHeight: 1,
              }}>
                {pendingCount}
              </span>
            )}
          </button>
          {canManageCoaches && (
            <button
              className={`btn ${activeTab === 'coaches' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('coaches')}
            >
              Coaches
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select
            className="coach-select"
            style={{ width: 160 }}
            value={filterDomain}
            onChange={(e) => { setFilterDomain(e.target.value); setSelectedLearner(null); }}
          >
            {domainOptions.map((d) => (
              <option key={d} value={d}>{d === 'all' ? 'All Domains' : d.charAt(0).toUpperCase() + d.slice(1).replace(/_/g, ' ')}</option>
            ))}
          </select>
          {activeTab === 'coaches' ? (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddCoach(true)}>
              + Add Coach
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setShowEnroll(true)}>
              + Enroll Directly
            </button>
          )}
        </div>
      </div>

      {/* ── ENROLLED TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'enrolled' && (
        <div style={{ display: 'grid', gridTemplateColumns: selectedLearner ? '1fr 380px' : '1fr', gap: 20 }}>
          <div className="coach-section" style={{ margin: 0 }}>
            {/* How learners are assigned explanation */}
            <div style={{
              background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 8, padding: '10px 14px', marginBottom: 14,
              fontSize: '0.83rem', color: '#94a3b8', lineHeight: 1.5,
            }}>
              <strong style={{ color: '#a78bfa' }}>How learners appear here:</strong> Any learner
              enrolled in one of your domains shows up automatically. Learners can request access
              from the domain page — you'll see those in the <strong>Access Requests</strong> tab.
              You can also enroll someone directly using <strong>+ Enroll Directly</strong>.
            </div>

            <div style={{ marginBottom: 14 }}>
              <input
                className="coach-input"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 320 }}
              />
            </div>

            {learnersLoading ? (
              <div className="coach-loading">Loading learners…</div>
            ) : filtered.length === 0 ? (
              <div className="coach-empty">
                <span className="coach-empty-icon">👥</span>
                <div className="coach-empty-title">No learners enrolled yet</div>
                <div className="coach-empty-desc">
                  Use <strong>+ Enroll Directly</strong> to add a learner by email, or approve
                  requests in the <strong>Access Requests</strong> tab.
                </div>
              </div>
            ) : (
              <table className="coach-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    {isAdmin && <th>Role</th>}
                    <th>Enrolled Domains</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr
                      key={l.learner_id}
                      style={{ background: selectedLearner?.learner_id === l.learner_id ? 'rgba(79,142,247,0.08)' : undefined, cursor: 'pointer' }}
                      onClick={() => viewStats(l)}
                    >
                      <td style={{ fontWeight: 600 }}>{l.display_name}</td>
                      <td className="text-muted text-sm">{l.email}</td>
                      {isAdmin && (
                        <td>
                          <select
                            className="coach-select"
                            style={{ width: 130, fontSize: '0.8rem', padding: '2px 6px' }}
                            value={l.role || 'learner'}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleChangeAccountRole(l.learner_id, e.target.value)}
                          >
                            <option value="learner">Learner</option>
                            <option value="coach_learner">Coach + Learner</option>
                            <option value="coach">Coach</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                      )}
                      <td>
                        {(l.enrolled_domains || []).map((d) => (
                          <span key={d} className="badge badge-draft" style={{ marginRight: 4, textTransform: 'capitalize' }}>{d.replace(/_/g, ' ')}</span>
                        ))}
                        {!(l.enrolled_domains || []).length && <span className="text-muted text-sm">—</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); viewStats(l); }}>
                            View Stats
                          </button>
                          <Link
                            className="btn btn-outline btn-sm"
                            to={`/coach/learners/${l.learner_id}/adaptive`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          >
                            Adaptive Detail
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Stats panel */}
          {selectedLearner && (
            <div className="coach-section" style={{ margin: 0, alignSelf: 'start' }}>
              <div className="coach-section-header">
                <h2 className="coach-section-title">{selectedLearner.display_name}</h2>
                <button className="btn btn-outline btn-sm" onClick={() => setSelectedLearner(null)}>✕</button>
              </div>
              <div className="text-muted text-sm" style={{ marginBottom: 12 }}>{selectedLearner.email}</div>

              {/* ── Subscription Tier per Domain ─────────────────────────── */}
              {(selectedLearner.enrolled_domains || []).length > 0 && (
                <div style={{
                  background: 'rgba(99,102,241,0.06)',
                  border: '1px solid rgba(99,102,241,0.2)',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#a78bfa', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Domain Access
                  </div>
                  {/* Show all coach-managed domains, not just learner's enrolled ones */}
                  {myDomains.map((d) => {
                    const enrolledDomains = selectedLearner.enrolled_domains || [];
                    const isEnrolled = enrolledDomains.includes(d);
                    const currentTier = (selectedLearner.domain_tiers || {})[d];
                    const effectiveTier = isEnrolled ? (currentTier || 'free') : 'none';
                    const isPaid = effectiveTier === 'active' || effectiveTier === 'trial';
                    const isFree = isEnrolled && !isPaid;
                    const isNone = !isEnrolled;
                    const saveKey = `${selectedLearner.learner_id}:${d}`;
                    const saveStatus = tierSaveState[saveKey];
                    const isSaving = saveStatus === 'saving';
                    const justSaved = saveStatus === 'saved';

                    const btnBase = {
                      padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600,
                      borderRadius: 4, cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.6 : 1, transition: 'all 0.15s',
                    };
                    return (
                      <div key={d} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: '0.82rem', color: '#cbd5e1', textTransform: 'capitalize' }}>
                          {d.replace(/_/g, ' ')}
                        </span>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {justSaved && (
                            <span style={{ fontSize: '0.72rem', color: '#4ade80', fontWeight: 600, marginRight: 4 }}>✓</span>
                          )}
                          {/* No Access */}
                          <button
                            onClick={() => !isSaving && handleSetSubscriptionTier(selectedLearner.learner_id, d, 'none')}
                            disabled={isSaving}
                            title="Remove learner from this domain"
                            style={{
                              ...btnBase,
                              border: '1px solid rgba(239,68,68,0.4)',
                              background: isNone ? 'rgba(239,68,68,0.18)' : 'transparent',
                              color: isNone ? '#f87171' : '#64748b',
                            }}
                          >
                            {isSaving && isNone ? '…' : 'No Access'}
                          </button>
                          {/* Free */}
                          <button
                            onClick={() => !isSaving && handleSetSubscriptionTier(selectedLearner.learner_id, d, 'free')}
                            disabled={isSaving}
                            title="Grant free-tier access (locked content visible but gated)"
                            style={{
                              ...btnBase,
                              border: '1px solid rgba(148,163,184,0.4)',
                              background: isFree ? 'rgba(148,163,184,0.2)' : 'transparent',
                              color: isFree ? '#94a3b8' : '#64748b',
                            }}
                          >
                            {isSaving && isFree ? '…' : 'Free'}
                          </button>
                          {/* Paid */}
                          <button
                            onClick={() => !isSaving && handleSetSubscriptionTier(selectedLearner.learner_id, d, 'active')}
                            disabled={isSaving}
                            title="Grant full paid access"
                            style={{
                              ...btnBase,
                              border: '1px solid rgba(34,197,94,0.4)',
                              background: isPaid ? 'rgba(34,197,94,0.15)' : 'transparent',
                              color: isPaid ? '#4ade80' : '#64748b',
                            }}
                          >
                            {isSaving && isPaid ? '…' : 'Paid'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {myDomains.length === 0 && (
                    <div style={{ fontSize: 12, color: '#475569' }}>
                      No domains assigned to your coach account yet.
                    </div>
                  )}
                </div>
              )}

              {statsLoading && <div className="coach-loading" style={{ padding: 24 }}>Loading stats…</div>}

              {stats && !statsLoading && (
                stats.error ? (
                  <div className="coach-error">
                    No stats yet. The learner may not have completed any sessions.
                  </div>
                ) : (
                  <>
                    <div className="coach-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                      {[
                        { label: 'Sessions', value: stats.stats?.total_sessions ?? 0 },
                        { label: 'Accuracy', value: stats.stats?.overall_accuracy != null ? `${Math.round(stats.stats.overall_accuracy * 100)}%` : '—' },
                        { label: 'Modules Mastered', value: stats.modules_mastered ?? 0 },
                        { label: 'Streak', value: stats.stats?.current_streak ?? 0 },
                      ].map(({ label, value }) => (
                        <div key={label} className="coach-stat-card" style={{ padding: 14 }}>
                          <span className="stat-value" style={{ fontSize: '1.4rem' }}>{value}</span>
                          <span className="stat-label">{label}</span>
                        </div>
                      ))}
                    </div>

                    {(stats.mastery_records || []).length > 0 ? (
                      <>
                        <div className="coach-label" style={{ marginBottom: 8 }}>Module Mastery</div>
                        {stats.mastery_records.map((m) => (
                          <div key={m.module_id} style={{ marginBottom: 10 }}>
                            <div className="flex-between" style={{ marginBottom: 4 }}>
                              <span className="text-sm" style={{ fontWeight: 600 }}>{m.module_id}</span>
                              <span className="text-sm text-muted">
                                {Math.round((m.best_accuracy || 0) * 100)}% {m.is_mastered && '✅'}
                              </span>
                            </div>
                            <div className="progress-bar-track">
                              <div className="progress-bar-fill" style={{ width: `${Math.round((m.best_accuracy || 0) * 100)}%`, background: m.is_mastered ? 'var(--coach-success)' : undefined }} />
                            </div>
                            <div className="text-muted text-sm" style={{ marginTop: 2 }}>{m.attempts} attempt{m.attempts !== 1 ? 's' : ''}</div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="coach-empty" style={{ padding: '12px 0' }}>
                        <div className="coach-empty-desc">No mastery records yet.</div>
                      </div>
                    )}
                  </>
                )
              )}

              {/* Skill mastery always shown when a learner is selected — has its own no-data fallback */}
              {!statsLoading && selectedLearner?.learner_id && (
                <>
                  <div className="coach-label" style={{ marginBottom: 8, marginTop: 16 }}>
                    Skill Mastery &amp; Review State
                  </div>
                  <SkillMasteryTable learnerId={selectedLearner.learner_id} />
                </>
              )}

              {/* Review Card Health (FSRS) panel — Phase 6 */}
              {!statsLoading && selectedLearner?.learner_id && (
                <>
                  <div className="coach-label" style={{ marginBottom: 8, marginTop: 20 }}>
                    Review Card Health (FSRS)
                  </div>
                  <LearnerSkillCardsPanel
                    learnerId={selectedLearner.learner_id}
                    domain={
                      filterDomain !== 'all'
                        ? filterDomain
                        : (selectedLearner.enrolled_domains || [])[0] || 'football'
                    }
                  />
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ACCESS REQUESTS TAB ───────────────────────────────────────────── */}
      {activeTab === 'requests' && (
        <div className="coach-section" style={{ margin: 0 }}>
          <HitLFeedPanel
            title="Pending Access Requests"
            items={requestItems}
            loading={requestsLoading}
            emptyIcon="📬"
            emptyTitle="No pending requests"
            emptyDesc='When learners click "Request Access" on a domain page, their request will appear here.'
            onApprove={(item) => handleApprove(item.id)}
            onReject={(item, reason) => handleReject(item.id, reason)}
            onRefresh={loadRequests}
          />
        </div>
      )}

      {/* ── COACHES TAB ────────────────────────────────────────────────────── */}
      {activeTab === 'coaches' && canManageCoaches && (
        <div className="coach-section" style={{ margin: 0 }}>
          <div style={{
            background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 8, padding: '10px 14px', marginBottom: 14,
            fontSize: '0.83rem', color: '#94a3b8', lineHeight: 1.5,
          }}>
            <strong style={{ color: '#a78bfa' }}>Coach Management:</strong>{' '}
            {isAdmin
              ? 'As a platform admin, you can add coaches to any domain and manage their roles.'
              : 'As a domain admin, you can add coaches to domains you manage.'}
            {' '}Use the domain filter above to switch between domains.
          </div>

          <div style={{ marginBottom: 14 }}>
            <input
              className="coach-input"
              placeholder="Search coaches by name or email…"
              value={coachSearch}
              onChange={(e) => setCoachSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>

          {filterDomain === 'all' && (
            <div style={{
              background: 'rgba(250,204,21,0.1)', border: '1px solid rgba(250,204,21,0.3)',
              borderRadius: 8, padding: '10px 14px', marginBottom: 14,
              fontSize: '0.83rem', color: '#fbbf24', lineHeight: 1.5,
            }}>
              Select a specific domain from the dropdown above to view and manage its coaches.
            </div>
          )}

          {filterDomain !== 'all' && coachesLoading && (
            <div className="coach-loading">Loading coaches…</div>
          )}

          {filterDomain !== 'all' && !coachesLoading && filteredCoaches.length === 0 && (
            <div className="coach-empty">
              <span className="coach-empty-icon">👤</span>
              <div className="coach-empty-title">No coaches assigned to this domain</div>
              <div className="coach-empty-desc">
                Use <strong>+ Add Coach</strong> to grant a user coaching access to this domain.
              </div>
            </div>
          )}

          {filterDomain !== 'all' && !coachesLoading && filteredCoaches.length > 0 && (
            <table className="coach-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Account Role</th>
                  <th>Domain Role</th>
                  <th>Granted By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredCoaches.map((c) => (
                  <tr key={c.coach_id}>
                    <td style={{ fontWeight: 600 }}>{c.display_name || '—'}</td>
                    <td className="text-muted text-sm">{c.email || '—'}</td>
                    <td>
                      <span className="badge badge-draft" style={{ textTransform: 'capitalize' }}>
                        {(c.account_role || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      <select
                        className="coach-select"
                        style={{ width: 140, fontSize: '0.8rem', padding: '2px 6px' }}
                        value={c.domain_role || 'domain_editor'}
                        onChange={(e) => handleChangeCoachDomainRole(c.coach_id, filterDomain, e.target.value)}
                      >
                        <option value="domain_admin">Domain Admin</option>
                        <option value="domain_editor">Domain Editor</option>
                        <option value="domain_viewer">Domain Viewer</option>
                      </select>
                    </td>
                    <td className="text-muted text-sm">{c.granted_by || '—'}</td>
                    <td>
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.3)' }}
                        onClick={() => handleRemoveCoach(c.coach_id, filterDomain)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </CoachLayout>
  );
}
